<template>
  <el-dialog
    v-model="dialogVisible"
    append-to-body
    class="link-lark-dialog"
    :close-on-click-modal="false"
    destroy-on-close
    :show-close="false"
    :width="600"
  >
    <template #header>
      <div class="link-lark-dialog__header">
        <div class="link-lark-dialog__title">{{ title }}</div>
        <button class="link-lark-dialog__close" type="button" @click="dialogVisible = false">
          <i class="icon-close-large-line t-iconfont"></i>
        </button>
      </div>
    </template>

    <div class="link-lark-dialog__body">
      <div class="link-lark-dialog__notice">
        <i class="icon-error-warning-fill link-lark-dialog__notice-icon t-iconfont"></i>
        <span class="link-lark-dialog__notice-text">{{ tipText }}</span>
      </div>

      <div class="link-lark-dialog__toolbar">
        <div class="link-lark-dialog__count">可关联群聊（{{ displayCount }}/{{ maxCount }}）</div>
        <div class="link-lark-dialog__actions">
          <button
            class="link-lark-dialog__action link-lark-dialog__action--primary"
            type="button"
            @click="handleCreateGroupChat"
          >
            新建群聊
          </button>
          <button
            class="link-lark-dialog__action"
            type="button"
            @click="handleLinkExistingGroupChat"
          >
            关联已有
          </button>
        </div>
      </div>

      <div class="link-lark-dialog__list">
        <template v-if="dataList.length">
          <div v-for="item in dataList" :key="item.id" class="link-lark-dialog__list-item">
            <div class="link-lark-dialog__item-header">
              <div class="link-lark-dialog__item-title">{{ item.groupName }}</div>
              <el-button
                class="link-lark-dialog__item-unbind"
                :loading="unbindingGroupId === item.id"
                type="text"
                @click="handleUnbind(item)"
              >
                解绑
              </el-button>
            </div>
            <div class="link-lark-dialog__item-field">
              <span class="link-lark-dialog__item-label">群聊会话 ID：</span>
              <span class="link-lark-dialog__item-value">{{ item.groupId }}</span>
            </div>
          </div>
        </template>
        <div v-else class="link-lark-dialog__list-empty">
          <ListEmpty title="暂未关联飞书群聊" />
        </div>
      </div>
    </div>
  </el-dialog>

  <Dialog v-model="unbindVisible" align-center title="解绑" width="400px">
    <i class="icon-error-warning-line t-iconfont" style="color: #ffb521; margin-right: 10px" />
    <span>解绑后将不再拉取该群聊内容，确定解绑吗</span>
    <template #footer>
      <div class="dialog-footer">
        <el-button @click="unbindVisible = false">取 消</el-button>
        <el-button color="#F54E4E" :loading="!!unbindingGroupId" @click="handleUnbindItem"
          >确 定</el-button
        >
      </div>
    </template>
  </Dialog>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import ListEmpty from '@/pages/ai-board/components/list-empty.vue'
import Dialog from '@/components/ai-dialog/index.vue'
import { getLarkGroupList, unbindLarkGroup } from '@/api/ai-board'
import AppMessage from '@/components/message/index.js'

defineOptions({
  name: 'LinkLarkDialog'
})

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  title: {
    type: String,
    default: '关联飞书群聊'
  },
  tipText: {
    type: String,
    default: '关联成功后，每周日晚24:00，将定时拉取已关联飞书群聊本周产生的聊天内容进行分析提炼。'
  },
  currentCount: {
    type: Number,
    default: 0
  },
  maxCount: {
    type: Number,
    default: 5
  },
  larkGroupParams: {
    type: Object,
    default: () => ({})
  }
})

const emits = defineEmits(['update:modelValue', 'createGroupChat', 'linkExistingGroupChat'])

const dialogVisible = computed({
  get: () => props.modelValue,
  set: (value) => emits('update:modelValue', value)
})

const dataList = ref([])
const userList = ref([])
const unbindingGroupId = ref('')
const unbindVisible = ref(false)
const unbindItem = ref(null)

const displayCount = computed(() => props.currentCount || dataList.value.length)

const getLarkGroupListData = () => {
  if (!props.larkGroupParams?.dataId) {
    dataList.value = []
    return
  }

  getLarkGroupList(props.larkGroupParams).then((res) => {
    const result = res.result || {}
    dataList.value = result.list || []
    userList.value = result.userList || []
  })
}

watch(
  () => props.modelValue,
  (visible) => {
    if (visible) {
      getLarkGroupListData()
    }
  }
)

const handleCreateGroupChat = () => {
  if (isReachMaxCount()) return

  emits('createGroupChat', userList.value)
}

const isReachMaxCount = () => {
  const reached = displayCount.value >= props.maxCount
  if (reached) {
    AppMessage.warning(`最多可关联${props.maxCount}个群聊`)
  }

  return reached
}

const handleLinkExistingGroupChat = () => {
  if (isReachMaxCount()) return

  emits('linkExistingGroupChat')
}

const handleUnbind = (item) => {
  if (!item?.id || unbindingGroupId.value) return

  unbindItem.value = item
  unbindVisible.value = true
}

const handleUnbindItem = async () => {
  if (!unbindItem.value?.id || unbindingGroupId.value) return

  unbindingGroupId.value = unbindItem.value.id
  try {
    const res = await unbindLarkGroup({
      id: unbindItem.value.id
    })

    if (res.success) {
      getLarkGroupListData()
    }
  } finally {
    unbindingGroupId.value = ''
    unbindItem.value = null
    unbindVisible.value = false
  }
}

defineExpose({
  refresh: getLarkGroupListData
})
</script>

<style lang="scss">
.link-lark-dialog {
  margin-top: 15vh;
  border-radius: 12px;
  box-shadow: 0px 2px 15px 2px rgba(0, 0, 0, 0.05);
}

.link-lark-dialog .el-dialog__header {
  border-bottom: none;
}

.link-lark-dialog .el-dialog__body {
  padding: 0;
}
</style>

<style lang="scss" scoped>
.link-lark-dialog__header {
  display: flex;
  align-items: center;
  padding: 24px;
}

.link-lark-dialog__title {
  flex: 1;
  font-family: PingFang SC;
  font-size: 18px;
  font-weight: 600;
  line-height: 26px;
  color: #323233;
}

.link-lark-dialog__close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  margin-left: 8px;
  font-size: 16px;
  color: #969799;
  background: transparent;
  border: none;
  cursor: pointer;
}

.link-lark-dialog__body {
  font-family: PingFang SC;
  padding: 0 24px;
  display: flex;
  flex-direction: column;
}

.link-lark-dialog__notice {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background: #e8f7ff;
  border-radius: 12px;
}

.link-lark-dialog__notice-icon {
  flex: 0 0 auto;
  margin-right: 8px;
  font-size: 20px;
  color: #248df5;
}

.link-lark-dialog__notice-text {
  flex: 1;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #323233;
}

.link-lark-dialog__toolbar {
  display: flex;
  align-items: center;
  margin: 16px 0;
}

.link-lark-dialog__count {
  flex: 1;
  font-size: 14px;
  font-weight: 400;
  line-height: 26px;
  color: #969799;
}

.link-lark-dialog__actions {
  display: flex;
  align-items: center;
}

.link-lark-dialog__action {
  min-width: 80px;
  height: 32px;
  padding: 0 12px;
  font-size: 14px;
  font-weight: 400;
  line-height: 30px;
  color: #646566;
  background: #ffffff;
  border: 1px solid #e5e6eb;
  border-radius: 8px;
  cursor: pointer;
}

.link-lark-dialog__action + .link-lark-dialog__action {
  margin-left: 12px;
}

.link-lark-dialog__action--primary {
  color: #ffffff;
  background: #7a52f4;
  border-color: #7a52f4;
}

.link-lark-dialog__list {
  flex: 0 0 auto;
  height: 400px;
  overflow-y: auto;
}

.link-lark-dialog__list-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.link-lark-dialog__list-item {
  padding: 16px;
  border: 1px solid #f2f3f5;
  border-radius: 16px;
}

.link-lark-dialog__list-item + .link-lark-dialog__list-item {
  margin-top: 12px;
}

.link-lark-dialog__list-item:last-child {
  margin-bottom: 12px;
}

.link-lark-dialog__item-header {
  display: flex;
  align-items: center;
}

.link-lark-dialog__item-title {
  flex: 1;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #323233;
}

.link-lark-dialog__item-unbind {
  color: #f54e4e;
  font-weight: normal;
}

.link-lark-dialog__item-unbind:hover,
.link-lark-dialog__item-unbind:focus,
.link-lark-dialog__item-unbind:active {
  color: #f54e4e;
}

.link-lark-dialog__item-unbind :deep(.el-icon),
.link-lark-dialog__item-unbind :deep(.el-loading-text) {
  color: #f54e4e;
}

.link-lark-dialog__item-field {
  display: flex;
  align-items: center;
  margin-top: 4px;
}

.link-lark-dialog__item-label {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #969799;
}

.link-lark-dialog__item-value {
  margin-left: 8px;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #323233;
}
</style>
